import { materialHasSubcategory } from '../utils/materialCategories';
import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { UserContext } from '../userContext';
import { FaSearch, FaPlay, FaFilePdf, FaImage, FaCalendarAlt, FaUser, FaEye, FaUserGraduate, FaCheckCircle } from 'react-icons/fa';
import BotaoNaoEncontrou from '../components/BotaoNaoEncontrou';
import SemanaDropdown from '../components/SemanaDropdown';
import NaturezaDropdown from '../components/NaturezaDropdown';
import './MateriaisPorSubcategoria.css';
import { normalizeText } from '../utils/text';

function MateriaisPorSubcategoria() {
  const { nomeDaSubcategoria } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [materiais, setMateriais] = useState([]);
  const [busca, setBusca] = useState('');
  const [naturezaFiltro, setNaturezaFiltro] = useState('');
  const [semanaFiltro, setSemanaFiltro] = useState('');
  const [loading, setLoading] = useState(true);

  // Função para verificar se um link é YouTube
  const isYouTubeLink = (url) => {
    if (!url) return false;
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  // Função para verificar se um link é Vimeo
  const isVimeoLink = (url) => {
    if (!url) return false;
    return url.includes('vimeo.com');
  };

  // Função para obter a URL do material considerando linkRelacionado
  const getMaterialUrl = (material) => {
    // Se é material de aluno e tem linkRelacionado com YouTube/Vimeo, usar ele
    if (material.source === 'student' && material.linkRelacionado) {
      if (isYouTubeLink(material.linkRelacionado) || isVimeoLink(material.linkRelacionado)) {
        return material.linkRelacionado;
      }
    }

    // Caso contrário, usar materialUrl normal
    return material.materialUrl;
  };

  // Função para obter o tipo de material considerando linkRelacionado
  const getMaterialType = (material) => {
    const materialUrl = getMaterialUrl(material);

    // Se tem linkRelacionado de YouTube/Vimeo, definir tipo baseado na URL
    if (material.source === 'student' && material.linkRelacionado && materialUrl === material.linkRelacionado) {
      if (isYouTubeLink(materialUrl)) return 'YOUTUBE';
      if (isVimeoLink(materialUrl)) return 'VIMEO';
    }

    // Caso contrário, usar materialType original
    return material.materialType?.toUpperCase();
  };

  const getSemanaLabel = (material) => {
    const rawSemana = material?.semana || material?.curso;
    if (!rawSemana) return '';

    const semana = String(rawSemana).trim();
    if (!semana) return '';

    const match = semana.match(/semana\s*(\d+)/i);
    return match ? `Semana ${match[1]}` : semana;
  };

  useEffect(() => {
    const fetchMateriais = async () => {
      setLoading(true);
      try {
        // Buscar materiais dos professores/admin
        const materialsRef = collection(db, 'materials');
        const materialsSnapshot = await getDocs(materialsRef);
        const materialsData = materialsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          source: 'admin'
        }));
        const materiaisFiltrados = materialsData.filter(m => materialHasSubcategory(m, nomeDaSubcategoria));

        // Buscar materiais aprovados dos alunos
        const studentMaterialsRef = collection(db, 'student_materials');
        const studentQuery = query(
          studentMaterialsRef,
          where('status', '==', 'aprovado')
        );
        const [studentResult] = await Promise.allSettled([getDocs(studentQuery)]);
        if (studentResult.status === 'rejected') {
          console.error('⚠ Erro ao carregar materiais de alunos:', studentResult.reason);
        }
        const studentDocs = studentResult.status === 'fulfilled'
          ? studentResult.value.docs.filter(doc => materialHasSubcategory(doc.data(), nomeDaSubcategoria))
          : [];
        const studentMaterials = studentDocs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.titulo,
            description: data.descricao,
            materialType: data.materialType,
            materialUrl: data.arquivoURL || data.videoUrl,
            linkRelacionado: data.linkRelacionado, // ADICIONADO: campo linkRelacionado
            coverImage: data.coverImage || data.arquivoURL || data.videoUrl,
            authorName: data.authorName,
            createdAt: data.createdAt,
            subcategory: data.subcategoria,
            category: data.categoria,
            source: 'student',
            conteudo: data.conteudo,
            resposta: data.resposta,
            videoResposta: data.videoResposta,
            hasThumbnail: data.hasThumbnail,
            semana: data.semana || data.curso || ''
          };
        });

        // Combinar e ordenar todos os materiais
        const todosMateriais = [...materiaisFiltrados, ...studentMaterials];
        todosMateriais.sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return dateB - dateA;
        });

        setMateriais(todosMateriais);
      } catch (error) {
        console.error('Erro ao carregar materiais:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMateriais();
  }, [nomeDaSubcategoria]);

  const normalizedBusca = normalizeText(busca);
  const normalizedNaturezaConteudo = normalizeText('Conteúdo');
  const normalizedNaturezaGabarito = normalizeText('Gabarito');
  const normalizedNaturezaResumo = normalizeText('Resumo');
  const normalizedNaturezaOutros = normalizeText('Outros');
  const normalizeNatureza = (value) => {
    const normalized = normalizeText(value || '');
    return normalized === normalizedNaturezaConteudo ? normalizedNaturezaGabarito : normalized;
  };
  const isNaturezaComSemana = (value) => {
    const normalized = normalizeNatureza(value);
    return (
      normalized === normalizedNaturezaGabarito ||
      normalized === normalizedNaturezaResumo ||
      normalized === normalizedNaturezaOutros
    );
  };
  const normalizedNaturezaFiltro = normalizeNatureza(naturezaFiltro);
  const normalizedSemanaFiltro = normalizeText(semanaFiltro);

  const materiaisFiltrados = materiais.filter(mat => {
    const matchesSearch =
      normalizeText(mat.title || '').includes(normalizedBusca) ||
      normalizeText(mat.description || '').includes(normalizedBusca) ||
      normalizeText(mat.authorName || '').includes(normalizedBusca);
    const matchesNatureza =
      !normalizedNaturezaFiltro ||
      normalizeNatureza(mat.naturezaMaterial || '') === normalizedNaturezaFiltro;
    const matchesWeek =
      !normalizedSemanaFiltro ||
      normalizeText(mat.semana || '') === normalizedSemanaFiltro;
    return matchesSearch && matchesNatureza && matchesWeek;
  });

  const handleVerConteudo = (material) => {
    navigate(`/conteudo/${material.id}`);
  };

  const getStaticPreview = (material) => {
    const materialType = getMaterialType(material); // USAR função que considera linkRelacionado
    const materialUrl = getMaterialUrl(material); // USAR função que considera linkRelacionado

    if (material.coverImage && material.coverImage !== materialUrl && material.hasThumbnail) {
      return material.coverImage;
    }

    if (material.coverImage && material.coverImage !== materialUrl) {
      return material.coverImage;
    }

    if (materialType === 'YOUTUBE' && materialUrl) {
      const videoId = materialUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/)?.[1];
      return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
    }

    if (materialType === 'VIMEO' && materialUrl) {
      const vimeoId = materialUrl.match(/vimeo\.com\/(\d+)/)?.[1];
      return vimeoId ? `https://i.vimeocdn.com/video/${vimeoId}_640x360.jpg` : null;
    }

    if (['PNG', 'JPG', 'JPEG'].includes(materialType) && materialUrl) {
      return materialUrl;
    }

    if (['PDF'].includes(materialType)) {
      return null;
    }

    return null;
  };

  const getCoverImage = (material) => {
    return getStaticPreview(material);
  };

  const getTypeIcon = (materialType) => {
    const type = materialType?.toUpperCase();
    switch (type) {
      case 'YOUTUBE':
      case 'VIMEO':
        return <FaPlay />;
      case 'PDF':
        return <FaFilePdf />;
      case 'PNG':
      case 'JPG':
      case 'JPEG':
        return <FaImage />;
      default:
        return <FaEye />;
    }
  };

  const getTypeBadgeClass = (materialType) => {
    const type = materialType?.toUpperCase();
    switch (type) {
      case 'YOUTUBE':
      case 'VIMEO':
        return 'type-video';
      case 'PDF':
        return 'type-pdf';
      case 'PNG':
      case 'JPG':
      case 'JPEG':
        return 'type-image';
      default:
        return 'type-default';
    }
  };

  const formatDate = (dateData) => {
    if (!dateData) return 'Data não disponível';

    let date;
    if (dateData.seconds) {
      date = new Date(dateData.seconds * 1000);
    } else if (dateData.toDate) {
      date = dateData.toDate();
    } else if (typeof dateData === 'string') {
      date = new Date(dateData);
    } else {
      date = new Date(dateData);
    }

    return date.toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="materiais-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando materiais...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="materiais-page">
      <header className="materiais-header">
        <h1 className="materiais-titulo">{nomeDaSubcategoria}</h1>
        <p className="materiais-subtitulo">{materiais.length} materiais disponíveis</p>
      </header>

      <div className="materiais-busca">
        <div className="search-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por título..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <div className="filter-container">
            <NaturezaDropdown
              value={naturezaFiltro}
              onChange={(value) => {
                setNaturezaFiltro(value);
                if (!isNaturezaComSemana(value)) setSemanaFiltro('');
              }}
            />
          </div>
          <div className="filter-container">
            <SemanaDropdown
              value={semanaFiltro}
              onChange={setSemanaFiltro}
              disabled={naturezaFiltro && !isNaturezaComSemana(naturezaFiltro)}
            />
          </div>
        </div>
      </div>

      <div className="materiais-content">
        {materiaisFiltrados.length > 0 ? (
          <div className="materiais-grid">
            {materiaisFiltrados.map((mat) => {
              const coverImage = getCoverImage(mat);
              const materialType = getMaterialType(mat); // USAR função que considera linkRelacionado
              const semanaLabel = getSemanaLabel(mat);

              return (
                <div className="material-card" key={`${mat.source}-${mat.id}`}>
                  <div className="cover-wrapper">
                    {coverImage ? (
                      <img
                        src={coverImage}
                        alt={mat.title}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const placeholder = e.target.nextSibling;
                          if (placeholder) placeholder.style.display = 'flex';
                        }}
                      />
                    ) : null}

                    <div className={`cover-placeholder ${getTypeBadgeClass(materialType)}`}
                      style={{ display: coverImage ? 'none' : 'flex' }}>
                      <div className="placeholder-icon">
                        {getTypeIcon(materialType)}
                      </div>
                      <div className="placeholder-text">
                        <span className="material-type">{materialType || 'CONTEÚDO'}</span>
                        <span className="material-title">{mat.title}</span>
                      </div>
                    </div>

                    <div className={`type-badge ${getTypeBadgeClass(materialType)}`}>
                      {getTypeIcon(materialType)}
                      <span>{materialType}</span>
                    </div>

                    {mat.source === 'student' ? (
                      <div className="student-badge">
                        <FaUserGraduate />
                        <span>Material do Aluno</span>
                      </div>
                    ) : (
                      <div className="expert-badge">
                        <FaCheckCircle />
                        <span>Especialista</span>
                      </div>
                    )}

                    {/* Indicador de Link Relacionado para materiais de aluno */}
                    {mat.source === 'student' && mat.linkRelacionado && (
                      <div
                        className="link-badge"
                        style={{
                          position: 'absolute',
                          bottom: '8px',
                          left: '8px',
                          background: 'rgba(156, 39, 176, 0.9)',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          zIndex: 2
                        }}
                      >
                        🔗 Link
                      </div>
                    )}

                    <div className="hover-overlay">
                      <button
                        className="preview-button"
                        onClick={() => handleVerConteudo(mat)}
                      >
                        <FaEye />
                        Ver Conteúdo
                      </button>
                    </div>
                  </div>

                  <div className="material-info">
                    <h2 className="material-title">{mat.title}</h2>
                    {semanaLabel && (
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '12px',
                          fontSize: '0.65rem',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                          whiteSpace: 'nowrap',
                          background: 'rgba(250, 204, 21, 0.95)',
                          color: '#111827',
                          border: '1px solid rgba(250, 204, 21, 0.55)',
                          boxShadow: '0 2px 6px rgba(250, 204, 21, 0.2)',
                          width: 'fit-content'
                        }}
                        title={`Conteudo referente a ${semanaLabel}`}
                      >
                        <span>{semanaLabel}</span>
                      </div>
                    )}

                    <div className="material-meta">
                      <div className="meta-item">
                        <FaUser className="meta-icon" />
                        <span>{mat.authorName || 'Conecta Admin'}</span>
                        {mat.source === 'student' ? (
                          <span className="author-type">(Aluno)</span>
                        ) : (
                          <span className="author-type-expert">
                            <span className="dot"></span>
                            (Especialista)
                          </span>
                        )}
                      </div>

                      <div className="meta-item">
                        <FaCalendarAlt className="meta-icon" />
                        <span>{formatDate(mat.createdAt)}</span>
                      </div>
                    </div>

                    <p className="material-description">
                      {mat.description?.length > 100
                        ? `${mat.description.substring(0, 100)}...`
                        : mat.description || 'Sem descrição disponível'
                      }
                    </p>

                    <button
                      className="ver-conteudo-button"
                      onClick={() => handleVerConteudo(mat)}
                    >
                      <FaEye />
                      Ver Conteúdo
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <FaSearch />
            </div>
            <h3>Nenhum material encontrado</h3>
            <p>
              {busca
                ? `Não encontramos materiais com "${busca}" nesta categoria.`
                : 'Esta categoria ainda não possui materiais disponíveis.'
              }
            </p>

            {/* BOTÃO "NÃO ENCONTROU O QUE PROCURA?" */}
            <BotaoNaoEncontrou
              termoBusca={busca || nomeDaSubcategoria}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default MateriaisPorSubcategoria;
